import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  AccountType,
  getV1CompaniesCompanyIdRatesRateIdAutoAddOnAssignments,
  ProjectState,
  RateOwnerType,
} from '@treadinc/horizon-api-spec';
import { RateType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { capitalize } from 'lodash-es';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';

import { AnimateComponentPresence } from '~components/AnimatePresence';
import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { CompanySelectorFormField } from '~components/FormFields/CompanySelectorFormField';
import { SwitchFormField } from '~components/FormFields/SwitchFormField';
import { TextFormField } from '~components/FormFields/TextFormField';
import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { SystemRoles } from '~constants/enums';
import { FormStateChangeProps } from '~formsShared/types';
import { Account } from '~hooks/useAccount/models';
import useAccount from '~hooks/useAccount/useAccount';
import { AddOnAssignment } from '~hooks/useAddOns/models';
import useProjects from '~hooks/useProjects/useProjects';
import { Rate } from '~hooks/useRates/models';
import { useRates } from '~hooks/useRates/useRates';
import { User } from '~hooks/useUsers/models';
import useUsers from '~hooks/useUsers/useUsers';
import { AutomaticAddOnsHandler } from '~pages/Sales/Orders/NewOrderForm/components/AutomaticAddOns';
import {
  rateFormValidationSchema,
  RatesFormSchemaInterface,
  setDefaultRateValues,
} from '~pages/Settings/RatesManagement/rateFormValidationSchema';
import {
  getRateTypeOptions,
  rateTypeLabelColor,
} from '~pages/Settings/RatesManagement/rateUtils';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';
import { usePrevious } from '~utils/hooks/usePrevious';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import AutomaticAddOns from './AutomaticAddOns';

interface RateFormProps {
  account?: Nullable<Account>;
  defaultRate: Rate;
  driver?: Nullable<User>;
  onFormStateChange: ({ isValid, isDirty }: FormStateChangeProps) => void;
  subFormsToHide: Array<RateType>;
  defaultRateOwnerType?: RateOwnerType;
}

export interface RateFormRef {
  submit: (callBack: (data: RatesFormSchemaInterface) => void) => Promise<void>;
}
const hourlyMinimumOptions = [
  `15 ${t('administration.rates.minutes_abbreviation')}`,
  `30 ${t('administration.rates.minutes_abbreviation')}`,
  `60 ${t('administration.rates.minutes_abbreviation')}`,
];
const hourlyIncrementOptions = [
  `1 ${t('administration.rates.minutes_abbreviation')}`,
  `15 ${t('administration.rates.minutes_abbreviation')}`,
  `30 ${t('administration.rates.minutes_abbreviation')}`,
];

export const RateForm = forwardRef<RateFormRef, RateFormProps>(function RateForm(
  {
    account,
    driver,
    defaultRate,
    onFormStateChange,
    subFormsToHide,
    defaultRateOwnerType,
  },
  ref,
) {
  const { userStore } = useStores();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isValid, isDirty },
    watch,
  } = useForm({
    resolver: yupResolver(rateFormValidationSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: setDefaultRateValues(
      defaultRate,
      account ?? null,
      driver ?? null,
      userStore.currentCompanies[0],
      defaultRate.ownerType ?? defaultRateOwnerType ?? RateOwnerType.CUSTOMER,
    ),
  });

  const automaticAddOnsRef = useRef<AutomaticAddOnsHandler>(null);

  const { getProjectsByCompanyIdTypeahead } = useProjects();
  const { getAccountsByCompanyIdTypeahead } = useAccount();
  const { getCompanyUsersTypeahead } = useUsers();
  const { getAccountRatesUsingOwnerType } = useRates();

  const dispatchAsCompanyId = getEffectiveUserCompanyId(userStore);
  const companyId = watch('company')?.id || dispatchAsCompanyId;
  const watchedOwnerType = watch('ownerType');
  const previousOwnerType = usePrevious(watchedOwnerType?.value);
  const watchedAccount = watch('account');
  const watchedDriver = watch('driver');

  const [isPrimaryDefined, setIsPrimaryDefined] = useState<boolean>(false);
  const [disabledFields, setDisabledFields] = useState<string[]>([]);

  const rateOwnerTypeOptions = Object.values(RateOwnerType).map((type) => ({
    label: t(`administration.rates.rate_owner_type_options.${type.toLowerCase()}`),
    value: type,
  }));

  useImperativeHandle(ref, () => ({
    submit: async (callBack: (data: RatesFormSchemaInterface) => void) => {
      const automaticAddOns = await (automaticAddOnsRef.current?.onSubmit() ??
        Promise.resolve({ addOns: [] }));

      handleSubmit((data) => {
        callBack({
          ...(data as RatesFormSchemaInterface),
          automaticAddOns:
            automaticAddOns.addOns as RatesFormSchemaInterface['automaticAddOns'],
        });
      })();
    },
  }));

  const fetchAccountsByCompanyId = useCallback(
    async (additionalProps = {}) => {
      if (!companyId) {
        return [];
      }
      return await getAccountsByCompanyIdTypeahead({
        companyId,
        accountTypes:
          watchedOwnerType?.value === RateOwnerType.CUSTOMER
            ? [AccountType.CUSTOMER]
            : [AccountType.VENDOR],
        ...additionalProps,
      });
    },
    [companyId, watchedOwnerType],
  );

  const fetchProjects = useCallback(
    async (args: { query: string; cursor: string }) => {
      return await getProjectsByCompanyIdTypeahead({
        companyId,
        query: args.query,
        cursor: args.cursor,
        limit: 15,
        states: [ProjectState.ACTIVE],
      });
    },
    [companyId, getProjectsByCompanyIdTypeahead],
  );

  const rateTypeOptions = getRateTypeOptions(
    watchedOwnerType?.value as RateOwnerType,
  ).filter((type) => !subFormsToHide.includes(type?.value as RateType));

  const typeWatchedOrDefault = defaultRate.type?.value?.length
    ? defaultRate.type?.value
    : watch('type')?.value;
  const isPrimaryDisabled = Boolean(disabledFields.includes('isPrimary'));

  useEffect(() => {
    const automaticAddOnsIsValid = automaticAddOnsRef.current?.isValid() ?? true;
    const automaticAddOnsIsDirty = automaticAddOnsRef.current?.isDirty() ?? false;

    onFormStateChange({
      isValid: isValid && automaticAddOnsIsValid,
      isDirty: isDirty || automaticAddOnsIsDirty,
    });
  }, [
    isValid,
    isDirty,
    automaticAddOnsRef.current?.isValid(),
    automaticAddOnsRef.current?.isDirty(),
  ]);

  useEffect(() => {
    if (
      watchedOwnerType?.value &&
      previousOwnerType &&
      watchedOwnerType.value !== previousOwnerType
    ) {
      setValue('account', { id: '', name: '' });
      setValue('driver', { id: '', firstName: '', lastName: '' });
    }
  }, [watchedOwnerType, previousOwnerType]);

  useEffect(() => {
    const loadRates = async () => {
      const params: Parameters<typeof getAccountRatesUsingOwnerType>[0] = {
        ownerType: watchedOwnerType?.value,
        companyId,
      };

      if (watchedOwnerType?.value === RateOwnerType.DRIVER) {
        params.driverId = watchedDriver?.id;
      } else {
        params.accountId = watchedAccount?.id;
      }

      const response = await getAccountRatesUsingOwnerType(params);
      if (response?.data?.length) {
        setIsPrimaryDefined(response.data.some((rate) => rate.isPrimary));
      } else {
        setIsPrimaryDefined(false);
      }
    };

    void loadRates();
  }, [watchedOwnerType, watchedAccount, watchedDriver]);

  useEffect(() => {
    const disabled = [];
    defaultRate.id?.length && disabled.push('type');
    isPrimaryDefined && disabled.push('isPrimary');
    setDisabledFields(disabled);
  }, [defaultRate.id, isPrimaryDefined]);

  const showRateChargeLogic = useMemo(
    () =>
      ![
        RateType.RATE_FLAT_COMMISSION,
        RateType.RATE_COMMISSION,
        RateType.RATE_FLAT_RATE,
      ].includes(typeWatchedOrDefault as RateType),
    [typeWatchedOrDefault],
  );

  const [addOnAssignments, setAddOnAssignments] = useState<AddOnAssignment[]>([]);
  const [isLoadingAddOnAssignments, setIsLoadingAddOnAssignments] = useState(false);

  const fetchAddOnAssignments = useCallback(async () => {
    if (defaultRate.id) {
      setIsLoadingAddOnAssignments(true);
      try {
        const response = await getV1CompaniesCompanyIdRatesRateIdAutoAddOnAssignments({
          path: { 'company-id': companyId, 'rate-id': defaultRate.id },
        });

        const addOnAssignments = response.data.data.map((addOnAssignment) =>
          AddOnAssignment.parse(addOnAssignment),
        );
        setAddOnAssignments(addOnAssignments);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoadingAddOnAssignments(false);
      }
    }
  }, [defaultRate.id, companyId]);

  useEffect(() => {
    fetchAddOnAssignments();
  }, [fetchAddOnAssignments]);

  return (
    <Box component="form" data-test-id="company-form">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          mb: 2,
          mt: 0.5,
        }}
      >
        <CompanySelectorFormField
          name={'company'}
          disabled={!!defaultRate?.company?.id}
          errors={errors}
          control={control}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1,
          mb: 2,
          mt: 0.5,
        }}
      >
        <AutocompleteFormField
          control={control}
          disabled={!!defaultRate.ownerType || !!defaultRateOwnerType}
          name="ownerType"
          errors={errors}
          multiple={false}
          isCheckEnabled={false}
          isRequired={true}
          list={rateOwnerTypeOptions}
          label={`${t('administration.rates.rate_owner_type')}`}
          getValue={(item) => item.value}
          getLabel={(item) => item?.label || ''}
        />
        {watchedOwnerType?.value === RateOwnerType.DRIVER ? (
          <AutocompleteAsyncFormField
            key={watchedOwnerType.value}
            control={control}
            clearable
            name="driver"
            errors={errors}
            filterSelectedOptions={false}
            getValue={(user: User) => user.id}
            getLabel={(user: User) => (user ? `${user.firstName} ${user.lastName}` : '')}
            label={`${t('administration.rates.rate_owner_type_options.driver')}`}
            asyncCallback={getCompanyUsersTypeahead}
            extraRequestOptions={{ companyId, roleName: SystemRoles.DRIVER }}
          />
        ) : (
          <AutocompleteAsyncFormField
            control={control}
            name="account"
            clearable
            errors={errors}
            asyncCallback={fetchAccountsByCompanyId}
            label={`${t(
              watchedOwnerType?.value === RateOwnerType.CUSTOMER
                ? 'administration.rates.rate_owner_type_options.customer'
                : 'administration.rates.rate_owner_type_options.vendor',
            )}`}
            getValue={(item) => item.id}
            getLabel={(item) => item.name || ''}
          />
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          alignItems: 'flex-start',
          mb: 1,
        }}
      >
        <TextFormField
          control={control}
          errors={errors}
          name="name"
          sx={{ mr: 1 }}
          label={`${t('form_fields.rate_name')}`}
          isRequired={true}
        />
        <AutocompleteFormField
          control={control}
          disabled={Boolean(disabledFields.includes('type'))}
          name="type"
          errors={errors}
          multiple={false}
          isCheckEnabled={false}
          isRequired={true}
          list={rateTypeOptions}
          label={`${t('administration.rates.rate_type')}`}
          getValue={(item) => item.value}
          getLabel={(item) => item?.label || ''}
        />
        <Box
          sx={{
            display: 'flex',
            height: '100%',
            minHeight: '75px',
            alignItems: 'center',
          }}
        >
          <Tooltip
            title={
              isPrimaryDisabled
                ? t('administration.rates.primary_rate_defined')
                : t('administration.rates.ser_as_primary')
            }
          >
            <span>
              <SwitchFormField
                labelPlacement={'start'}
                sx={{ ml: 1, minWidth: '190px' }}
                control={control}
                errors={errors}
                disabled={!defaultRate.isPrimary && isPrimaryDisabled}
                name="isPrimary"
                label={t('administration.rates.ser_as_primary') || ''}
                isRequired={false}
                value={defaultRate.isPrimary}
              />
            </span>
          </Tooltip>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <TextFormField
          sx={{ width: '30%' }}
          control={control}
          errors={errors}
          name={'rateInputs.rate'}
          label={`${
            typeWatchedOrDefault === RateType.RATE_COMMISSION
              ? t('administration.rates.commission_rate')
              : t('administration.rates.rate_value_label')
          }`}
          isRequired={true}
          type={'number'}
          InputProps={{
            endAdornment: (
              <Box sx={{ position: 'relative' }}>
                <InputAdornment position="start">
                  {typeWatchedOrDefault === RateType.RATE_COMMISSION ? '%' : ''}
                </InputAdornment>
              </Box>
            ),
          }}
        />
        {rateTypeLabelColor(typeWatchedOrDefault)?.label && (
          <Box
            sx={{
              ml: 2,
              display: 'flex',
              alignItems: 'center',
              mt: 2,
              color: 'text.secondary',
            }}
          >
            {rateTypeLabelColor(typeWatchedOrDefault)?.label}
          </Box>
        )}
      </Box>
      <AnimateComponentPresence
        isVisible={showRateChargeLogic}
        identifier={'rate-charge-logic'}
      >
        <>
          <Divider sx={{ mt: 2 }}></Divider>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              my: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
              }}
            >
              <Typography variant="form-section-title" sx={{ mb: 1 }}>
                {`${t('administration.rates.set_advanced_title')}`}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {typeWatchedOrDefault === RateType.RATE_PER_HOUR ? (
                <>
                  <AutocompleteFormField
                    control={control}
                    name={'rateInputs.minimum'}
                    limitTags={2}
                    errors={errors}
                    isRequired={true}
                    list={hourlyMinimumOptions}
                    multiple={false}
                    sx={{ width: '30%', mb: 1 }}
                    isCheckEnabled={false}
                    label={`${t('administration.rates.minimum_charged')}`}
                    getValue={(item) => item}
                    getLabel={(item) => capitalize(item)}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                    }}
                  >
                    <AutocompleteFormField
                      control={control}
                      name={'rateInputs.increment'}
                      limitTags={2}
                      errors={errors}
                      isRequired={true}
                      list={hourlyIncrementOptions}
                      multiple={false}
                      sx={{ width: '30%', mb: 1 }}
                      isCheckEnabled={false}
                      label={`${t('administration.rates.increment_by')}`}
                      getValue={(item) => item}
                      getLabel={(item) => capitalize(item)}
                    />
                  </Box>
                </>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <TextFormField
                    sx={{ mr: 1, width: '30%' }}
                    control={control}
                    errors={errors}
                    isRequired={true}
                    type={'number'}
                    name={'rateInputs.minimum'}
                    label={`${t('administration.rates.minimum_charged')}`}
                    InputProps={{
                      endAdornment: (
                        <Box sx={{ position: 'relative' }}>
                          {typeWatchedOrDefault?.length ? (
                            <InputAdornment
                              position="end"
                              sx={{ position: 'absolute', ml: 3 }}
                            >
                              {rateTypeLabelColor?.(typeWatchedOrDefault)?.label}
                            </InputAdornment>
                          ) : (
                            <></>
                          )}
                        </Box>
                      ),
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </>
      </AnimateComponentPresence>
      <Divider flexItem={true} sx={{ my: 2 }} orientation={'horizontal'}></Divider>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          mb: 1,
        }}
      >
        <Typography variant="form-section-title">{`${t('administration.rates.when_applied')}`}</Typography>
        <Typography variant={'caption'} color={'grey'}>
          {t('administration.rates.when_applied_sub_text')}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          mb: 1,
        }}
      >
        <AutocompleteAsyncFormField
          control={control}
          name="projects"
          limitTags={2}
          errors={errors}
          groupBy={(item) => item?.type}
          multiple={true}
          sx={{ width: '75%', mb: 1 }}
          label={`${t('form_fields.project')}`}
          getValue={(item) => item.id}
          getLabel={(item) => item.name || ''}
          asyncCallback={fetchProjects}
        />
      </Box>
      <Divider flexItem={true} sx={{ my: 2 }} orientation={'horizontal'}></Divider>
      <Typography variant="form-section-title" sx={{ mt: 2 }}>
        {t('administration.rates.automatic_add_ons.title')}
      </Typography>
      <LoadingSpinner isVisible={isLoadingAddOnAssignments} />
      <AutomaticAddOns
        ref={automaticAddOnsRef}
        sx={{ mt: 1 }}
        initialAddOns={addOnAssignments}
      />
    </Box>
  );
});
